import { Fragment } from "react";
import { Helmet } from "react-helmet-async";
import { ContentSection, PageHero } from "./Sections";
import { SmartLink } from "./SiteChrome";

export function PageSeo({ page, noindex = false }) {
  const canonical = `https://levelup360.pro${page.path ?? ""}`;
  return (
    <Helmet>
      <title>{page.seoTitle}</title>
      {page.seoDescription && <meta name="description" content={page.seoDescription} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, follow" />}
      <meta property="og:title" content={page.seoTitle} />
      {page.seoDescription && <meta property="og:description" content={page.seoDescription} />}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content="https://levelup360.pro/assets/images/levelup360-social-card.png" />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>
  );
}

export function ButtonRow({ ctas = [] }) {
  if (!ctas.length) return null;
  return (
    <div className="section-actions">
      {ctas.map((cta) => (
        <SmartLink key={cta.label} target={cta.target} className={`button ${cta.style === "outline" ? "button-outline" : "button-primary"}`}>
          {cta.label}
        </SmartLink>
      ))}
    </div>
  );
}

export function Anchors({ ids = [] }) {
  return ids.map((id) => <span className="section-anchor" id={id} key={id} aria-hidden="true" />);
}

export function List({ items, ordered = false, className = "" }) {
  if (!items?.length) return null;
  const Tag = ordered ? "ol" : "ul";
  return <Tag className={`${ordered ? "numbered-list" : "detail-list"} ${className}`}>{items.map((item) => <li key={item}>{item}</li>)}</Tag>;
}

function ContinuityFlow({ items }) {
  if (!items?.length) return null;
  return (
    <ol className="continuity-flow" aria-label="Connected delivery record">
      {items.map((item, index) => (
        <li key={item}><span>{String(index + 1).padStart(2, "0")}</span>{item}</li>
      ))}
    </ol>
  );
}

function SignalLine({ items, label = "Operating sequence" }) {
  if (!items?.length) return null;
  return (
    <ol className="signal-line" aria-label={label}>
      {items.map((item, index) => (
        <li key={item}><span>{String(index + 1).padStart(2, "0")}</span><strong>{item}</strong></li>
      ))}
    </ol>
  );
}

function EvidenceHighlights({ items }) {
  if (!items?.length) return null;
  return (
    <dl className="evidence-highlights">
      {items.map((item) => (
        <div key={item.value}>
          <dt>{item.value}</dt>
          <dd>{item.label}</dd>
        </div>
      ))}
    </dl>
  );
}

function ServiceStage({ section }) {
  const layout = section.layout ?? "decision";
  return (
    <>
      <SignalLine items={section.signals} label={`${section.stage} operating sequence`} />
      <div className={`stage-story stage-story-${layout}`}>
        <div className="stage-piece stage-problem">
          <p className="story-label">The problem</p>
          <p className="story-lead">{section.problem}</p>
        </div>
        <div className="stage-piece stage-response">
          <p className="story-label">How LevelUp360 helps</p>
          <p>{section.help}</p>
        </div>
        <div className={`stage-piece stage-outcome${section.tone === "mist" ? " stage-outcome-paper" : ""}`}>
          <p className="story-label">Standalone outcome</p>
          <p>{section.outcome}</p>
        </div>
      </div>
      <ButtonRow ctas={section.ctas} />
    </>
  );
}

function EditorialSection({ section }) {
  return (
    <>
      <div className="long-copy">
        {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        {section.intro && <p>{section.intro}</p>}
        <List items={section.bullets} />
        <List items={section.numbered} ordered />
        {section.closing && <p className="section-closing">{section.closing}</p>}
        {section.textLink && (
          <p><SmartLink className="text-link section-text-link" target={section.textLink.target}>{section.textLink.label}</SmartLink></p>
        )}
      </div>
      <ContinuityFlow items={section.flow} />
      <SignalLine items={section.signals} />
      <EvidenceHighlights items={section.highlights} />
      <ButtonRow ctas={section.ctas} />
    </>
  );
}

function FundingOptions({ section }) {
  return (
    <>
      <ul className="funding-options" aria-label="Funding options">
        {section.options.map((option) => (
          <li key={option.heading}>
            <h3>{option.heading}</h3>
            <p>{option.body}</p>
            <SmartLink className="text-link funding-link" target={option.cta.target}>{option.cta.label}</SmartLink>
          </li>
        ))}
      </ul>
      <p className="funding-qualification">{section.qualification}</p>
    </>
  );
}

export function StandardSections({ sections, pageVariant = "standard" }) {
  return sections.map((section) => {
    const layout = section.layout ?? (section.stage ? "decision" : "editorial");
    return (
      <Fragment key={section.heading}>
        <ContentSection className={`${section.stage ? "service-stage-section" : "standard-section"} page-${pageVariant} layout-${layout}`} tone={section.tone ?? "light"}>
          <Anchors ids={section.anchors} />
          <div className="section-heading">
            {section.stage && <p className="eyebrow">{section.stage}</p>}
            <h2>{section.heading}</h2>
          </div>
          {section.stage ? <ServiceStage section={section} /> : section.layout === "funding" ? <FundingOptions section={section} /> : <EditorialSection section={section} />}
        </ContentSection>
      </Fragment>
    );
  });
}

export function StandardPage({ page, newsletter }) {
  return (
    <>
      <PageSeo page={page} />
      <PageHero content={page.hero} variant={page.variant} />
      <StandardSections sections={page.sections} pageVariant={page.variant} />
      {newsletter}
    </>
  );
}
